import React, { useEffect, useState } from "react";
import { Card, Table, Badge, Alert, Row, Col } from "react-bootstrap";
import { format } from "date-fns";
import { fetchPendingSupplierOrders } from "./DashboardService";
import IconShow from "../../images/fi_eye.png";
import IconEdit from "../../images/edit.png";
import "./PendingSupplierOrders.css";

const PendingSupplierOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPendingOrders = async () => {
      try {
        setLoading(true);
        const data = await fetchPendingSupplierOrders();
        // Limiter à 15 commandes maximum
        setOrders(data.slice(0, 15));
      } catch (err) {
        console.error("Erreur lors du chargement des commandes urgentes:", err);
        setError("Impossible de charger les commandes urgentes");
      } finally {
        setLoading(false);
      }
    };

    loadPendingOrders();

    // Auto-refresh toutes les 30 secondes pour mettre à jour les compteurs
    const interval = setInterval(() => {
      loadPendingOrders();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case 4:
        return <Badge bg="primary">En cours de validation</Badge>;
      case 3:
        return <Badge bg="info">En préparation</Badge>;
      case 1:
        return <Badge bg="warning">En cours de livraison</Badge>;
      default:
        return <Badge bg="secondary">Inconnu</Badge>;
    }
  };

  const isOrderLate = (deliveryDate) => {
    const now = new Date();
    const delivery = new Date(deliveryDate);
    return delivery < now;
  };

  const isOrderToday = (deliveryDate) => {
    const today = new Date();
    const delivery = new Date(deliveryDate);

    return (
      delivery.getDate() === today.getDate() &&
      delivery.getMonth() === today.getMonth() &&
      delivery.getFullYear() === today.getFullYear() &&
      !isOrderLate(deliveryDate)
    );
  };

  // Calculer le niveau d'urgence basé sur la proximité de la livraison
  const getUrgencyLevel = (deliveryDate) => {
    const now = new Date();
    const delivery = new Date(deliveryDate);
    const hoursUntilDelivery = (delivery - now) / (1000 * 60 * 60);

    // En retard
    if (hoursUntilDelivery < 0) {
      return { level: 'critical', color: 'danger', emoji: '🔴', show: true };
    }

    // Livraison dans < 4h
    if (hoursUntilDelivery < 4) {
      return { level: 'urgent', color: 'danger', emoji: '🔴', show: true };
    }

    // Livraison aujourd'hui (< 24h)
    if (hoursUntilDelivery < 24) {
      return { level: 'warning', color: 'warning', emoji: '🟡', show: true };
    }

    // Livraison demain (24-48h)
    if (hoursUntilDelivery < 48) {
      return { level: 'info', color: 'info', emoji: '🔵', show: true };
    }

    // Livraison > 2 jours - pas d'urgence
    return { level: 'normal', color: 'secondary', emoji: '🟢', show: false };
  };

  // Formater le temps restant jusqu'à la livraison
  const formatTimeUntilDelivery = (deliveryDate) => {
    const now = new Date();
    const delivery = new Date(deliveryDate);
    const diffMs = delivery - now;

    if (diffMs < 0) {
      // En retard
      const hours = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60));
      const minutes = Math.floor((Math.abs(diffMs) % (1000 * 60 * 60)) / (1000 * 60));
      if (hours === 0) return `RETARD de ${minutes}min`;
      return `RETARD de ${hours}h${minutes > 0 ? minutes : ''}`;
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours < 1) {
      return `Dans ${minutes}min`;
    } else if (hours < 24) {
      return `Dans ${hours}h${minutes > 0 ? minutes : ''}`;
    } else if (hours < 48) {
      return `Demain ${format(delivery, 'HH:mm')}`;
    } else {
      return format(delivery, 'dd/MM à HH:mm');
    }
  };

  // Calculer le temps passé dans le statut actuel
  const getTimeInStatus = (order) => {
    // Si on a l'historique des statuts, on utilise la date de changement
    // Sinon on utilise DateCommande comme approximation
    const statusStartDate = order.DateCommande; // TODO: utiliser l'historique si disponible
    const now = new Date();
    const diffMs = now - new Date(statusStartDate);
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) return `${mins}min`;
    return `${hours}h${mins > 0 ? mins : ''}`;
  };

  // Déterminer si le temps dans le statut est anormal
  const isStatusTimeAbnormal = (order) => {
    const urgency = getUrgencyLevel(order.DateLivraison);
    const timeInStatusMs = new Date() - new Date(order.DateCommande);
    const minutesInStatus = timeInStatusMs / (1000 * 60);

    // Seuils selon l'urgence
    if (urgency.level === 'critical' || urgency.level === 'urgent') {
      // Si urgent ou en retard, validation > 30min = problème
      return order.StatuCommande === 4 && minutesInStatus > 30;
    } else if (urgency.level === 'warning') {
      // Si aujourd'hui, validation > 2h = problème
      return order.StatuCommande === 4 && minutesInStatus > 120;
    } else if (urgency.level === 'info') {
      // Si demain, validation > 4h = problème
      return order.StatuCommande === 4 && minutesInStatus > 240;
    }

    return false;
  };


  if (loading) {
    return (
      <Card className="mb-4 border-warning border-2">
        <Card.Body>
          <Card.Title>⚠️ Livraisons en Attente - Urgentes</Card.Title>
          <p>Chargement...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="mb-4">
        {error}
      </Alert>
    );
  }

  if (orders.length === 0) {
    return (
      <Card className="mb-4 border-success border-2">
        <Card.Body>
          <Card.Title>✅ Livraisons en Attente</Card.Title>
          <p className="text-muted">Aucune commande urgente pour le moment</p>
        </Card.Body>
      </Card>
    );
  }

  // Filtrer les commandes pour la vue mobile
  const lateOrders = orders.filter(order => isOrderLate(order.DateLivraison));
  const todayOrders = orders.filter(order => isOrderToday(order.DateLivraison));

  // Les 5 commandes en cours de validation (statut 4), de la plus ancienne à la plus récente
  const validationOrders = orders
    .filter(order => order.StatuCommande === 4)
    .sort((a, b) => new Date(a.DateCommande) - new Date(b.DateCommande))
    .slice(0, 5);

  return (
    <Row>
      {/* Commandes en retard */}
      {lateOrders.length > 0 && (
        <Col xs={12} lg={4} className="mb-4">
          <Card className="h-100 border-danger border-2">
            <Card.Body>
              <Card.Title className="d-flex align-items-center">
                <span className="me-2">🔴</span>
                <span>Commandes en Retard</span>
                <Badge bg="danger" className="ms-2">{lateOrders.length}</Badge>
              </Card.Title>

              <div>
                {lateOrders.map((order) => {
                  const ville = order.AdresseLivraison?.split(",")[1]?.trim() || "—";
                  const urgency = getUrgencyLevel(order.DateLivraison);
                  const timeUntilDelivery = formatTimeUntilDelivery(order.DateLivraison);
                  const timeInStatus = getTimeInStatus(order);
                  const isAbnormal = isStatusTimeAbnormal(order);

                  const clientName = order.client_type === 'fournisseur'
                    ? order.fournisseur_profil?.user_profil?.RaisonSocial
                    : [order.NomClient, order.PrenomClient].filter(Boolean).join(' ') || order.fournisseur_profil?.user_profil?.RaisonSocial;

                  return (
                    <Card key={order.id} className="mb-2 border-danger mobile-order-card">
                      <Card.Body className="p-2">
                        <Row className="align-items-center">
                          <Col xs={9}>
                            <div className="mb-1">
                              <strong className="text-danger">#{order.id}</strong>
                              <span className="ms-2 small">{clientName || "—"}</span>
                            </div>

                            {/* Temps jusqu'à livraison */}
                            <div className="small mb-1">
                              <span className="fw-bold text-danger">{urgency.emoji} {timeUntilDelivery}</span>
                            </div>

                            {/* Temps dans le statut avec alerte si anormal */}
                            <div className="small text-muted mb-1">
                              {getStatusBadge(order.StatuCommande)}
                              {isAbnormal && (
                                <span className="ms-1 text-warning">⚠️ {timeInStatus}</span>
                              )}
                            </div>

                            <div className="small text-muted">
                              <span>{ville}</span>
                            </div>
                          </Col>
                          <Col xs={3} className="text-end">
                            <div className="d-flex flex-column gap-1">
                              <a href={`/commande-fournisseur/show/${order.id}`} className="btn btn-sm btn-outline-primary p-1">
                                <img src={IconShow} width="16px" alt="Voir" />
                              </a>
                              <a href={`/commande-fournisseur/edit/${order.id}`} className="btn btn-sm btn-outline-secondary p-1">
                                <img src={IconEdit} width="16px" alt="Modifier" />
                              </a>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </Col>
      )}

      {/* Commandes à livrer aujourd'hui */}
      {todayOrders.length > 0 && (
        <Col xs={12} lg={4} className="mb-4">
          <Card className="h-100 border-info border-2">
            <Card.Body>
              <Card.Title className="d-flex align-items-center">
                <span className="me-2">📦</span>
                <span>Livraisons Aujourd'hui</span>
                <Badge bg="info" className="ms-2">{todayOrders.length}</Badge>
              </Card.Title>

              <div>
                {todayOrders.map((order) => {
                  const ville = order.AdresseLivraison?.split(",")[1]?.trim() || "—";
                  const urgency = getUrgencyLevel(order.DateLivraison);
                  const timeUntilDelivery = formatTimeUntilDelivery(order.DateLivraison);
                  const timeInStatus = getTimeInStatus(order);
                  const isAbnormal = isStatusTimeAbnormal(order);

                  const clientName = order.client_type === 'fournisseur'
                    ? order.fournisseur_profil?.user_profil?.RaisonSocial
                    : [order.NomClient, order.PrenomClient].filter(Boolean).join(' ') || order.fournisseur_profil?.user_profil?.RaisonSocial;

                  return (
                    <Card key={order.id} className="mb-2 border-info mobile-order-card">
                      <Card.Body className="p-2">
                        <Row className="align-items-center">
                          <Col xs={9}>
                            <div className="mb-1">
                              <strong className="text-info">#{order.id}</strong>
                              <span className="ms-2 small">{clientName || "—"}</span>
                            </div>

                            {/* Temps jusqu'à livraison avec urgence */}
                            {urgency.show && (
                              <div className="small mb-1">
                                <span className={`fw-bold text-${urgency.color}`}>
                                  {urgency.emoji} {timeUntilDelivery}
                                </span>
                              </div>
                            )}

                            {/* Temps dans le statut */}
                            <div className="small text-muted mb-1">
                              {getStatusBadge(order.StatuCommande)}
                              {isAbnormal && (
                                <span className="ms-1 text-warning">⚠️ {timeInStatus}</span>
                              )}
                            </div>

                            <div className="small text-muted">
                              <span>{ville}</span>
                            </div>
                          </Col>
                          <Col xs={3} className="text-end">
                            <div className="d-flex flex-column gap-1">
                              <a href={`/commande-fournisseur/show/${order.id}`} className="btn btn-sm btn-outline-primary p-1">
                                <img src={IconShow} width="16px" alt="Voir" />
                              </a>
                              <a href={`/commande-fournisseur/edit/${order.id}`} className="btn btn-sm btn-outline-secondary p-1">
                                <img src={IconEdit} width="16px" alt="Modifier" />
                              </a>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </Col>
      )}

      {/* Commandes en cours de validation */}
      {validationOrders.length > 0 && (
        <Col xs={12} lg={4} className="mb-4">
          <Card className="h-100 border-primary border-2">
            <Card.Body>
              <Card.Title className="d-flex align-items-center">
                <span className="me-2">✓</span>
                <span>En Cours de Validation</span>
                <Badge bg="primary" className="ms-2">{validationOrders.length}</Badge>
              </Card.Title>
              <p className="text-muted small mb-2">Les plus anciennes en attente</p>

              <div>
                {validationOrders.map((order) => {
                  const ville = order.AdresseLivraison?.split(",")[1]?.trim() || "—";
                  const urgency = getUrgencyLevel(order.DateLivraison);
                  const timeUntilDelivery = formatTimeUntilDelivery(order.DateLivraison);
                  const timeInStatus = getTimeInStatus(order);
                  const isAbnormal = isStatusTimeAbnormal(order);

                  const clientName = order.client_type === 'fournisseur'
                    ? order.fournisseur_profil?.user_profil?.RaisonSocial
                    : [order.NomClient, order.PrenomClient].filter(Boolean).join(' ') || order.fournisseur_profil?.user_profil?.RaisonSocial;

                  return (
                    <Card key={order.id} className="mb-2 border-primary mobile-order-card">
                      <Card.Body className="p-2">
                        <Row className="align-items-center">
                          <Col xs={9}>
                            <div className="mb-1">
                              <strong className="text-primary">#{order.id}</strong>
                              <span className="ms-2 small">{clientName || "—"}</span>
                            </div>

                            {/* Temps jusqu'à livraison (si urgence) */}
                            {urgency.show && (
                              <div className="small mb-1">
                                <span className={`fw-bold text-${urgency.color}`}>
                                  {urgency.emoji} {timeUntilDelivery}
                                </span>
                              </div>
                            )}

                            {/* Afficher date de livraison si pas urgente */}
                            {!urgency.show && (
                              <div className="small text-muted mb-1">
                                <strong>Livraison:</strong> {order.DateLivraison ? format(new Date(order.DateLivraison), "dd/MM/yyyy HH:mm") : "—"}
                              </div>
                            )}

                            {/* Temps dans le statut avec alerte */}
                            <div className="small text-muted mb-1">
                              {getStatusBadge(order.StatuCommande)}
                              <span className={`ms-1 ${isAbnormal ? 'text-warning' : ''}`}>
                                {isAbnormal && '⚠️ '}{timeInStatus}
                              </span>
                            </div>

                            <div className="small text-muted">
                              <span>{ville}</span>
                            </div>
                          </Col>
                          <Col xs={3} className="text-end">
                            <div className="d-flex flex-column gap-1">
                              <a href={`/commande-fournisseur/show/${order.id}`} className="btn btn-sm btn-outline-primary p-1">
                                <img src={IconShow} width="16px" alt="Voir" />
                              </a>
                              <a href={`/commande-fournisseur/edit/${order.id}`} className="btn btn-sm btn-outline-secondary p-1">
                                <img src={IconEdit} width="16px" alt="Modifier" />
                              </a>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default PendingSupplierOrders;
